import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import * as S from '~/components/Chat/Chat01/TextBox/TextBox.styled';
import { Icon } from '~/components/atomic';
import { useDartCache, useDecreaseDartMutation } from '~/features/dart';
import { useChatInput, useModal } from '~/hooks';
import {
  ChatAllRoomType,
  MessageRequest,
  MessageResponse,
  MissionGameChatSendMessageRequest,
  ModalParams,
} from '~/types';
import { GAEvent, SendMessage, getUuidV4, isInApp } from '~/utils';
import { airbridgeEvent } from '~/utils/airbridge';
import { GA } from '~/constants';
import { breakpoint } from '~/styles/themes';
import Direction from '~/assets/img/icons/direction.svg';
import { useCommonChatStore, useGroupChatStore, useUserStore } from '~/store';
import useDartBottomSheetStore from '~/store/useDartBottomSheetStore';
import GroupChatText from './GroupChatText';

export interface TextBoxProps {
  webSocket: React.RefObject<WebSocket>;
  chatroomId: string;
  setWaitingChatTrue: () => void;
  setMessages: React.Dispatch<React.SetStateAction<MessageResponse[]>>;
  room_type?: ChatAllRoomType;
  isActiveInput?: boolean;
  setIsActiveInput?: React.Dispatch<React.SetStateAction<boolean>>;
  showTextBox?: boolean;
  setShowTextBox?: React.Dispatch<React.SetStateAction<boolean>>;
  safe_chat?: boolean;
  waitingChat?: boolean;
  receiverCharacterId?: string;
}

export default function ChatTextBox({
  webSocket,
  chatroomId,
  setWaitingChatTrue,
  setMessages,
  room_type,
  isActiveInput,
  setIsActiveInput,
  showTextBox,
  setShowTextBox,
  safe_chat = false,
  waitingChat,
  receiverCharacterId,
}: TextBoxProps) {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const [isWaitingChat, setIsWaitingChat] = useState<boolean>(false);

  const email = useUserStore((state) => state.email);
  const members = useCommonChatStore((state) => state.chatMembers);
  const receiverIdRef = useRef(receiverCharacterId);

  const setIsTextFocus = useCommonChatStore((state) => state.setIsTextFocus);
  const { isChatLayoutShow, setIsChatLayoutShow } = useCommonChatStore(
    (state) => ({
      isChatLayoutShow: state.isChatLayoutShow,
      setIsChatLayoutShow: state.setIsChatLayoutShow,
    }),
  );

  const { showSendButton, setShowSendButton } = useGroupChatStore((state) => ({
    showSendButton: state.showSendButton,
    setShowSendButton: state.setShowSendButton,
  }));

  const { mutate: decreaseDart, isPending: isDecreasing } =
    useDecreaseDartMutation();

  const { getCurrentDartCount } = useDartCache();

  const safeChatRef = useRef<boolean>(safe_chat);
  const [sf, setSf] = useState<boolean>(safe_chat);

  useEffect(() => {
    safeChatRef.current = safe_chat;
    setSf(safe_chat);
  }, [safe_chat, sf]);

  useEffect(() => {
    safeChatRef.current = safe_chat;
  }, []);

  useEffect(() => {
    setIsWaitingChat(waitingChat ?? false);
  }, [waitingChat]);

  useEffect(() => {
    receiverIdRef.current = receiverCharacterId;
  }, [receiverCharacterId]);

  const {
    setValue,
    valueRef,
    inpRef,
    insertHtmlAtCursor,
    updateInputValue,
    setMentionedAIMembers,
    handleDirectionMsg,
    handlePaste,
  } = useChatInput(members, setShowSendButton);

  const { setIsOpen } = useDartBottomSheetStore();

  const handleSendMessage = () => {
    const currentReceiverId = receiverIdRef.current;
    if (!inpRef.current) return;
    const currentDartCount = getCurrentDartCount();

    if (currentDartCount <= 0) {
      if (isInApp()) {
        const ModalRequest: ModalParams = {
          title: 'Get More Darts!',
          content:
            'You’re out of Darts for chatting. To continue, Click to Continue.',
          buttonName: 'Get Darts',
          click: () => setIsOpen(true),
        };
        openModal(ModalRequest);
      } else {
        const ModalRequest: ModalParams = {
          title: 'Get More Darts!',
          content:
            'You’re out of Darts for chatting. To continue, Click to App download.',
          buttonName: 'Get Darts',
          click: () => (window.location.href = 'https://app.hey-d.ai/app'),
        };
        openModal(ModalRequest);
      }
    } else {
      if (valueRef.current.trim().length > 0) {
        if (!room_type) return;
        const messageRequestData =
          room_type === 'mission'
            ? ({
                action: 'gameChatSendMessage',
                email,
                game_chat_id: Number(chatroomId),
                message: valueRef.current,
                character_id: currentReceiverId,
                safe_chat: safeChatRef.current,
                chat_id: getUuidV4(),
              } as MissionGameChatSendMessageRequest)
            : ({
                action: 'sendMessage',
                message: valueRef.current,
                chatroom_id: chatroomId,
                email,
                chat_id: getUuidV4(),
                canvas_type: import.meta.env.VITE_CHATROOM_BACKGROUND,
                character_type: room_type,
                safe_chat: safeChatRef.current,
              } as MessageRequest);

        if (webSocket) {
          SendMessage(webSocket, messageRequestData, setMessages);
          const chatType =
            room_type === 'mystery'
              ? 'chat_game'
              : room_type === 'mission'
              ? 'game'
              : 'chat';

          // send GA event : SEND_MESSAGE
          GAEvent(email, GA.ACTION.SEND_MESSAGE, {
            chat_id: chatroomId,
            chat_type: chatType,
          });
          airbridgeEvent.sendMessage(email, chatType);
        }

        if (setWaitingChatTrue) {
          setWaitingChatTrue();
        }

        setShowSendButton(false);
        setValue('');
        setMentionedAIMembers([]);
        decreaseDart();
        if (inpRef.current) {
          inpRef.current.innerHTML = '';
        }

        // startCountdown(sendAIMessage);
        if (setShowTextBox) setShowTextBox(false);
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    if (event.key === 'Backspace') {
      const range = selection.getRangeAt(0);
      if (
        range.startContainer.nodeType === Node.TEXT_NODE &&
        range.startContainer.parentElement?.className === 'mention'
      ) {
        event.preventDefault();
        const parentElement = range.startContainer.parentElement;
        parentElement.remove();
        updateInputValue();
      }
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();

      if (event.isComposing) {
        return;
      }

      handleSendMessage();
    } else if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault();
      insertHtmlAtCursor('\n<br>');
      updateInputValue();
      if (inpRef.current) {
        inpRef.current.scrollTop = inpRef.current.scrollHeight;
      }
    }
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    updateInputValue();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
  };

  useEffect(() => {
    const inpElem = inpRef.current;
    const handleFocus = (event: FocusEvent) => {
      setIsTextFocus(true);
      setIsChatLayoutShow(true);
    };
    const handleBlur = (event: FocusEvent) => {
      const newFocusElement = event.relatedTarget as HTMLElement;
      if (
        newFocusElement &&
        (newFocusElement.closest('.direction-button') ||
          newFocusElement.closest('.chat-text'))
      ) {
        event.preventDefault();
      } else {
        setIsTextFocus(false);
      }
    };
    if (inpElem && members.length > 0) {
      inpElem.addEventListener('keydown', handleKeyDown);
      inpElem.addEventListener('keyup', handleKeyUp);
      inpElem.addEventListener('paste', handlePaste);
      inpElem.addEventListener('focus', handleFocus);
      inpElem.addEventListener('blur', handleBlur);

      if (isActiveInput) {
        inpElem.focus();

        if (setIsActiveInput) {
          setIsActiveInput(false);
        }
      }
    }
    return () => {
      if (inpElem) {
        inpElem.removeEventListener('keydown', handleKeyDown);
        inpElem.removeEventListener('keyup', handleKeyUp);
        inpElem.removeEventListener('paste', handlePaste);
        inpElem.removeEventListener('focus', handleFocus);
        inpElem.addEventListener('blur', handleBlur);
      }
    };
  }, [members, isActiveInput]);

  return (
    <div
      css={css`
        display: block;
        position: fixed;
        z-index: 10;
        bottom: 0;
        left: 50%;
        width: calc(100% - 16px);
        max-width: ${breakpoint.mobile_large};
        padding-bottom: 4px;
        transform: translateX(-50%);
      `}
    >
      <S.TextContainer showTextBox={showTextBox}>
        <S.TextWrapper>
          <div className='chat-text'>
            <GroupChatText inpRef={inpRef} />
          </div>
          <S.ButtonWrapper>
            <S.ButtonBox>
              <S.DirectionButton
                className='direction-button'
                onMouseDown={handleDirectionMsg}
              >
                <img src={Direction} alt='' />
              </S.DirectionButton>
            </S.ButtonBox>
            <S.ButtonBox>
              <S.SendButton
                disabled={!showSendButton}
                onClick={handleSendMessage}
              >
                <Icon
                  size={24}
                  color={showSendButton ? 'content_dark_1' : 'content_light_5'}
                  iconName='Send'
                />
              </S.SendButton>
            </S.ButtonBox>
          </S.ButtonWrapper>
        </S.TextWrapper>
      </S.TextContainer>
    </div>
  );
}
